// views/notice/NoticeRegisterView.tsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { createNoticeApi, uploadImageApi, NoticeCreateRequest } from '../../lib/api/notice';
import './NoticeForm.css';

// 서버 업로드 어댑터 클래스
class ServerUploadAdapter {
    private loader: any;

    constructor(loader: any) {
        this.loader = loader;
    }

    upload() {
        return this.loader.file.then((file: File) => new Promise<{default: string}>((resolve, reject) => {
            console.log('CKEditor image upload 시작:', file.name, file.size, file.type);

            uploadImageApi(file)
            .then(data => {
                console.log('업로드 응답 데이터:', data);
                if (data.url) {
                    const imageUrl = `${import.meta.env.VITE_APP_API_URL}${data.url}`;
                    console.log('최종 이미지 URL:', imageUrl);
                    resolve({
                        default: imageUrl
                    });
                } else {
                    console.error('응답 데이터에 URL이 없습니다:', data);
                    reject('이미지 URL을 받을 수 없습니다.');
                }
            })
            .catch(error => {
                console.error('Image upload failed:', error);
                reject(`이미지 업로드에 실패했습니다: ${error.message || error}`);
            });
        }));
    }

    abort() {
        console.log('이미지 업로드 중단');
    }
}

const NoticeRegisterView = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeTab, setActiveTab] = useState('공지사항');
    
    // 폼 데이터
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        author: '관리자',
        isFixed: false,
        attachments: [] as File[]
    });

    // 에러 상태
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleTabChange = (tab: string) => {
        if (tab === "관리자정보 등록") {
            navigate("/admin");
            return;
        }
        setActiveTab(tab);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            return {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
        });

        // 에러 메시지 제거
        if (errors[name]) {
            setErrors(prev => {
                return {
                    ...prev,
                    [name]: ''
                };
            });
        }
    };

    const handleEditorChange = (event: any, editor: any) => {
        const data = editor.getData();
        setFormData(prev => {
            return {
                ...prev,
                content: data
            };
        });

        // 에러 메시지 제거
        if (errors.content) {
            setErrors(prev => {
                return {
                    ...prev,
                    content: ''
                };
            });
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setFormData(prev => {
            return {
                ...prev,
                attachments: [...prev.attachments, ...files]
            };
        });
    };

    const removeFile = (index: number) => {
        setFormData(prev => {
            return {
                ...prev,
                attachments: prev.attachments.filter((_, i) => i !== index)
            };
        });
    };

    const validate = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = '제목을 입력해주세요.';
        }

        if (!formData.content.trim()) {
            newErrors.content = '내용을 입력해주세요.';
        }

        return newErrors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            // API 요청 데이터 준비
            const noticeData: NoticeCreateRequest = {
                title: formData.title,
                content: formData.content,
                author: formData.author || '관리자', // 기본값 설정
                isFixed: formData.isFixed
            };

            // 공지사항 등록 API 호출
            await createNoticeApi(noticeData, formData.attachments);
            
            alert('공지사항이 성공적으로 등록되었습니다.');
            navigate('/notice');
        } catch (error: any) {
            console.error('등록 실패:', error);
            alert('공지사항 등록에 실패했습니다. 다시 시도해주세요.');
        }
    };

    const handleCancel = () => {
        if (formData.title || formData.content || formData.attachments.length > 0) {
            if (window.confirm('작성중인 내용이 모두 사라집니다. 취소하시겠습니까?')) {
                navigate('/notice');
            }
        } else {
            navigate('/notice');
        }
    };

    // CKEditor 설정 - 이미지 업로드 포함
    const editorConfig = {
        toolbar: [
            'heading',
            '|',
            'bold',
            'italic',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'blockQuote',
            'link',
            'insertTable',
            '|',
            'imageUpload',
            '|',
            'undo',
            'redo'
        ],
        licenseKey: 'GPL',
        image: {
            toolbar: [
                'imageTextAlternative',
                '|',
                'imageStyle:inline',
                'imageStyle:wrapText',
                'imageStyle:breakText'
            ]
        }
    };

    // 업로드 어댑터를 설정하는 플러그인 함수
    function CustomUploadAdapterPlugin(editor: any) {
        editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
            return new ServerUploadAdapter(loader);
        };
    }

    return (
        <div className="notice-form-container">
            {/* 탭 버튼 */}
            <div className="tab-buttons">
                <div className="" style={{ float: "left" }}>
                    <button
                        className={`tab-button ${activeTab === '공지사항' ? 'active' : ''}`}
                        onClick={() => handleTabChange('공지사항')}
                    >
                        공지사항
                    </button>
                    <button
                        className={`tab-button ${activeTab === '관리자정보 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('관리자정보 등록')}
                    >
                        관리자정보 등록
                    </button>
                    <button
                        className={`tab-button ${activeTab === '로고 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('로고 등록')}
                    >
                        로고 등록
                    </button>
                </div>
            </div>

            {/* 컨텐츠 박스 */}
            <div className="notice-content-box">
                <div className="notice-content">
                    {/* 헤더 */}
                    <div className="content-header">
                        <h1 className="page-title">공지사항 등록</h1>
                    </div>

                    {/* 폼 영역 */}
                    <form onSubmit={handleSubmit}>
                        <table className="form-table">
                            <tbody>
                            {/* 제목 */}
                            <tr>
                                <td className="label-cell">제목 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder="제목을 입력하세요"
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                </td>
                            </tr>

                            {/* 상단고정 */}
                            <tr>
                                <td className="label-cell">상단고정</td>
                                <td className="input-cell">
                                    <label className="checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            name="isFixed"
                                            checked={formData.isFixed}
                                            onChange={handleInputChange}
                                            className="form-checkbox"
                                        />
                                        <span className="checkbox-text">상단에 고정하여 표시</span>
                                    </label>
                                </td>
                            </tr>

                            {/* 첨부파일 */}
                            <tr>
                                <td className="label-cell">첨부파일</td>
                                <td className="input-cell">
                                    <div className="file-upload-section">
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="file-upload-btn"
                                        >
                                            파일 선택
                                        </button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            multiple
                                            style={{ display: 'none' }}
                                            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.hwp,.txt"
                                        />
                                        <span className="file-help-text">
                                            (이미지, PDF, 문서파일 등 업로드 가능)
                                        </span>
                                    </div>
                                    
                                    {/* 첨부파일 목록 */}
                                    {formData.attachments.length > 0 && (
                                        <div className="attached-files">
                                            {formData.attachments.map((file, index) => (
                                                <div key={index} className="file-item">
                                                    <span className="file-name">{file.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(index)}
                                                        className="file-remove-btn"
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </td>
                            </tr>

                            {/* 내용 */}
                            <tr>
                                <td className="label-cell content-label">내용 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <div className="editor-wrapper">
                                        <div style={{ padding: '10px', background: '#333', border: '1px solid #555', borderRadius: '4px', marginBottom: '10px', fontSize: '12px', color: '#ccc' }}>
                                            CKEditor 로딩 중... 만약 계속 표시되지 않으면 페이지를 새로고침해주세요.
                                        </div>
                                        <CKEditor
                                            editor={ClassicEditor as any}
                                            config={{
                                                ...editorConfig,
                                                extraPlugins: [CustomUploadAdapterPlugin]
                                            }}
                                            data={formData.content}
                                            onChange={handleEditorChange}
                                            onReady={editor => {
                                                console.log('CKEditor ready:', editor);
                                                // 로딩 메시지 숨기기
                                                const loadingMsg = document.querySelector('.editor-wrapper > div') as HTMLElement;
                                                if (loadingMsg) {
                                                    loadingMsg.style.display = 'none';
                                                }
                                            }}
                                            onError={(error, { willEditorRestart }) => {
                                                console.error('CKEditor error:', error);
                                                if (willEditorRestart) {
                                                    console.log('Editor will restart');
                                                }
                                            }}
                                        />
                                    </div>
                                    {errors.content && <span className="error-message">{errors.content}</span>}
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        {/* 버튼 영역 */}
                        <div className="form-actions">
                            <button type="button" onClick={handleCancel} className="btn-cancel">
                                취소
                            </button>
                            <button type="submit" className="btn-submit">
                                등록하기
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NoticeRegisterView;